import { Checkbox } from "../ui/checkbox";
import { Info, Lock, Plus, X } from "lucide-react";
import { vatFromCountry } from "../../lib/ukCountry";
import { Choice, Field, Notice, Section, TextArea, TextInput } from "./fields";
import { DeadlineNote, deadlineBlocks } from "./DeadlineNote";
import { TimelinePreview } from "./TimelinePreview";
import { CountrySelect } from "./CountrySelect";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const pad = (n) => String(n).padStart(2, "0");

// VAT is not a question: it follows the invoicing country. Nothing is
// said when the address is outside the UK.
function VatNote({ country }) {
  const vat = vatFromCountry(country);
  if (vat === "No") return null;
  const text = vat === null
    ? "Tell us the country so that we know whether UK VAT applies to your invoices."
    : "UK VAT at 20% will be added to your invoices because your invoicing address is in the United Kingdom.";
  return (
    <p className={`flex items-start gap-2 text-sm leading-relaxed ${vat === null ? "text-stone-500" : "text-[#1B4F8A]"}`} data-testid="cm-vat-note" data-vat={vat || "unknown"}>
      <Info className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{text}</span>
    </p>
  );
}

// One invoicing address is required; the "+" adds further addresses that
// also receive the invoices (written to the SOW and to Cognito).
function InvoiceEmails({ a, set, bad }) {
  const extra = a.invoice_emails_extra || [];
  const update = (i, v) => set("invoice_emails_extra", extra.map((e, j) => (j === i ? v : e)));
  const remove = (i) => set("invoice_emails_extra", extra.filter((_, j) => j !== i));
  return (
    <Field label="Email address for invoices" required invalid={bad("invoice_email") || bad("invoice_emails_extra")} group className="sm:col-start-2"
      hint="Accounts payable, or the inbox invoices should reach. Use + to add further addresses that should also receive the invoices.">
      <TextInput type="email" value={a.invoice_email} onChange={(v) => set("invoice_email", v)} placeholder="accounts@company.com" testid="cm-invoice-email" />
      {extra.map((e, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`cm-invoice-email-extra-${i}`}>
          <TextInput type="email" value={e} onChange={(v) => update(i, v)} placeholder="colleague@company.com" testid={`cm-invoice-email-extra-input-${i}`} />
          <button type="button" onClick={() => remove(i)} aria-label="Remove this address" data-testid={`cm-invoice-email-remove-${i}`}
            className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full border border-stone-300 text-stone-500 hover:border-red-400 hover:text-red-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      {extra.length < 10 && (
        <button type="button" onClick={() => set("invoice_emails_extra", [...extra, ""])} data-testid="cm-invoice-email-add"
          className="self-start inline-flex items-center gap-1.5 text-sm font-medium text-[#1B4F8A] hover:underline underline-offset-4">
          <Plus className="w-4 h-4" /> Add another email address
        </button>
      )}
    </Field>
  );
}

// Purchase order: when the client record says whether POs are used, that
// answer is taken as read and a single link lets the client override it
// for this project. Without a record the client is asked outright.
function PoSection({ n, a, set, onFile, bad, forSigning = true }) {
  const known = onFile?.use_po === "Yes" || onFile?.use_po === "No";
  const onFileYes = onFile?.use_po === "Yes";
  const company = onFile?.company || "your organisation";
  const usesPo = a.uses_po === "yes";
  const differs = known && a.uses_po && usesPo !== onFileYes;
  const toggle = (v) => { set("uses_po", v); if (v === (onFileYes ? "yes" : "no")) set("po_update_record", false); };
  const btnCls = "self-start inline-flex items-center rounded-full border-2 border-[#1B4F8A] bg-white px-5 py-2.5 text-sm font-semibold text-[#1B4F8A] transition-[background-color,color,transform] hover:bg-[#1B4F8A] hover:text-white active:scale-[0.98]";
  // Route B: the client is unlikely to hold the PO yet, so we only ask
  // whether one will be required and make the start condition explicit.
  const intro = forSigning
    ? "Many organisations need a purchase order number on every invoice. Let us know whether yours does."
    : "You are unlikely to have the purchase order yet, so we only need to know whether one will be required.";
  return (
    <Section n={n} title="Purchase order" intro={known ? undefined : intro}>
      {known && (
        <Notice tone="info" title={`Our records show that ${company} ${onFileYes ? "uses" : "does not use"} purchase order numbers`} testid="cm-po-on-file">
          {forSigning
            ? (onFileYes
              ? "Enter the purchase order number for this project below, or leave it blank if it is still being raised. If this project does not need one, use the button under the box; the change applies to this project only unless you tell us otherwise."
              : "Nothing is needed here. If this project does need a purchase order number, use the button below; the change applies to this project only unless you tell us otherwise.")
            : (onFileYes
              ? "We will therefore expect a purchase order for this project. If this project does not need one, use the button below; the change applies to this project only unless you tell us otherwise."
              : "We therefore do not expect a purchase order for this project. If this project does need one, use the button below; the change applies to this project only unless you tell us otherwise.")}
        </Notice>
      )}
      {!known && (
        <Field label={forSigning ? "Does your organisation use purchase order numbers?" : "Will a purchase order be required for this project?"} required group invalid={bad("uses_po")}>
          <Choice value={a.uses_po} onChange={(v) => set("uses_po", v)} options={forSigning ? [["yes", "Yes, we use purchase orders"], ["no", "No, we do not"]] : [["yes", "Yes, a purchase order will be required"], ["no", "No, a purchase order is not required"]]} testid="cm-uses-po" />
        </Field>
      )}
      {forSigning && usesPo && (
        <Field label="Purchase order number" invalid={bad("po_number")} hint="If the PO is still being raised, leave this blank: you can sign now and send the number to your Medicys contact when you have it. Medicys will not invoice before receiving it.">
          <TextInput value={a.po_number} onChange={(v) => set("po_number", v)} placeholder="PO-00000" testid="cm-po" />
        </Field>
      )}
      {!forSigning && (
        <Notice tone="warning" title="When the work can start" testid="cm-po-start-condition">
          Work cannot commence until the contract or Statement of Work has been signed by both parties
          {usesPo ? " and Medicys has received the corresponding purchase order. Please send the purchase order to your Medicys contact as soon as it is raised." : a.uses_po === "no" ? "." : " and, where one is required, Medicys has received the corresponding purchase order."}
        </Notice>
      )}
      {known && !differs && (
        <button type="button" onClick={() => toggle(onFileYes ? "no" : "yes")} className={btnCls} data-testid="cm-po-override">
          {onFileYes ? "PO not required" : "This project requires a PO"}
        </button>
      )}
      {differs && (
        <>
          <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-950 leading-relaxed" data-testid="cm-po-override-note">
            <p>Noted: {usesPo ? "a purchase order number is needed for this project" : "no purchase order number is needed for this project"}, which differs from our records for {company}.</p>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="cm-po-update-record">
              <Checkbox checked={!!a.po_update_record} onCheckedChange={(v) => set("po_update_record", !!v)} className="mt-0.5" data-testid="cm-po-update-record-box" />
              <span className="font-bold uppercase underline underline-offset-2">If this is a permanent change, please tick the box and we will update our records accordingly</span>
            </label>
          </div>
          <button type="button" onClick={() => toggle(onFileYes ? "yes" : "no")} className={btnCls} data-testid="cm-po-override-undo">
            {onFileYes ? "A purchase order is needed after all" : "No purchase order after all"}
          </button>
        </>
      )}
    </Section>
  );
}

// Fields common to both routes: the Medicys Statement of Work needs
// them for the document, the client paper route needs them even when
// their paper governs. Mirrors the Cognito Commissioning block. ``startAt``
// sets the first section number so the host step can count on.
// ``invalid`` is the set of field keys the server reported as missing.
// First name and surname as two boxes; the combined ``fullKey`` value is
// what the document, Cognito and the emails use.
export const splitName = (full) => {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? [parts.slice(0, -1).join(" "), parts[parts.length - 1]] : [parts[0] || "", ""];
};

function NameFields({ a, set, invalid, firstKey = "signatory_first_name", lastKey = "signatory_last_name", fullKey = "signatory_name",
  labelFirst = "Your first name", labelLast = "Your surname", required = true, hint = null, testid = "cm-signatory" }) {
  const [first, last] = [a[firstKey], a[lastKey]];
  const [dFirst, dLast] = first === undefined && last === undefined ? splitName(a[fullKey]) : [first || "", last || ""];
  const update = (f, l) => {
    set(firstKey, f);
    set(lastKey, l);
    set(fullKey, `${f.trim()} ${l.trim()}`.trim());
  };
  return (
    <>
      <Field label={labelFirst} required={required} invalid={invalid && !dFirst.trim()} hint={hint}>
        <TextInput value={dFirst} onChange={(v) => update(v, dLast)} placeholder="First name" testid={`${testid}-first-name`} autoComplete={fullKey === "signatory_name" ? "given-name" : "off"} />
      </Field>
      <Field label={labelLast} required={required} invalid={invalid && !dLast.trim()}>
        <TextInput value={dLast} onChange={(v) => update(dFirst, v)} placeholder="Surname" testid={`${testid}-last-name`} autoComplete={fullKey === "signatory_name" ? "family-name" : "off"} />
      </Field>
    </>
  );
}

export function SharedDetailsFields({ a, set: rawSet, forSigning, startAt = 1, onFile = null, invalid = new Set(), deadline = null, onDeadlineCheck = null, checking = false, locked = false, onUnlock = null, legalEntity = false, timeline = null }) {
  // Locked: every field above the agreement box is read only once the
  // client has ticked it; the fieldset disables the controls and ``set``
  // ignores stray changes.
  const set = locked ? () => {} : rawSet;
  const addr = a.invoice_address || {};
  const setAddr = (k) => (v) => set("invoice_address", { ...addr, [k]: v });
  const toggleDay = (d) => {
    const cur = a.update_frequency || [];
    if (cur.includes(d)) return set("update_frequency", cur.filter((x) => x !== d));
    if (cur.length >= 2) return;
    set("update_frequency", [...cur, d]);
  };
  const daysFull = (a.update_frequency || []).length >= 2;
  const bad = (k) => invalid.has(k);
  const addrBad = bad("invoice_address");
  const blocked = deadlineBlocks(a, deadline);
  let n = startAt;
  return (
    <div className="flex flex-col gap-8 min-w-0">
      {locked && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-[#1B4F8A]/30 bg-[#1B4F8A]/5 px-4 py-3 text-sm text-[#1B4F8A]" data-testid="cm-locked-notice">
          <Lock className="w-4 h-4 shrink-0" />
          <span className="flex-1">Your details are locked because you have confirmed your agreement below. Unlock them if you need to change anything, then tick the agreement box again to sign.</span>
          {onUnlock && (
            <button type="button" onClick={onUnlock} className="self-start sm:self-auto shrink-0 inline-flex items-center rounded-full border border-[#1B4F8A] bg-white px-4 py-1.5 text-sm font-semibold text-[#1B4F8A] hover:bg-[#1B4F8A] hover:text-white transition-colors" data-testid="cm-unlock">
              Unlock to edit
            </button>
          )}
        </div>
      )}
    <fieldset
      disabled={locked}
      data-testid="cm-shared-fields"
      data-locked={locked ? "true" : "false"}
      className={`flex flex-col gap-8 min-w-0 ${locked ? "[&_input:disabled]:bg-stone-100 [&_input:disabled]:text-stone-700 [&_input:disabled]:border-stone-200 [&_input:disabled]:cursor-default [&_textarea:disabled]:bg-stone-100 [&_textarea:disabled]:text-stone-700 [&_textarea:disabled]:cursor-default [&_button:disabled]:cursor-default [&_button:disabled]:opacity-80" : ""}`}
    >
      <Section n={pad(n++)} title="About you" intro="The person completing this on behalf of your organisation. We use these details on the document and for any questions.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NameFields a={a} set={set} invalid={bad("signatory_name")} />
          <Field label="Your work email" required invalid={bad("signatory_email")}><TextInput type="email" value={a.signatory_email} onChange={(v) => set("signatory_email", v)} placeholder="name@company.com" testid="cm-signatory-email" autoComplete="email" /></Field>
          {legalEntity && (
            <Field label="Legal entity that will contract with Medicys" required invalid={bad("legal_entity_name")}><TextInput value={a.legal_entity_name} onChange={(v) => set("legal_entity_name", v)} testid="cp-entity" autoComplete="organization" /></Field>
          )}
          <Field label="Your internal reference for this project" required invalid={bad("client_reference")} hint="A project code or name your colleagues will recognise on invoices and updates."><TextInput value={a.client_reference} onChange={(v) => set("client_reference", v)} testid="cm-client-ref" /></Field>
          <Field label="What deadline will Medicys work to?" required={forSigning} invalid={bad("project_deadline")} hint="The date you need the last of the deliverables."><TextInput type="date" value={a.project_deadline} onChange={(v) => set("project_deadline", v)} testid="cm-deadline" /></Field>
          {forSigning && timeline && (
            <Field label="When do you expect to give Medicys the go-ahead?" invalid={bad("planned_start")} hint={`Optional. The date you expect ${timeline.trigger || "to confirm the start"}, which the schedule counts from.`}>
              <TextInput type="date" value={a.planned_start} onChange={(v) => set("planned_start", v)} testid="cm-planned-start" />
            </Field>
          )}
        </div>
        {forSigning && <TimelinePreview timeline={timeline} deadline={a.project_deadline} />}
        <DeadlineNote a={a} deadline={deadline} onCheck={onDeadlineCheck} checking={checking} />
        {blocked && (
          <p className="text-sm text-stone-500" data-testid="cm-deadline-blocked-note">
            The remaining sections of this form appear once the deadline is resolved.
          </p>
        )}
      </Section>

      {!blocked && (
        <>
      {forSigning && <PoSection n={pad(n++)} a={a} set={set} onFile={onFile} bad={bad} forSigning={forSigning} />}

      <Section n={pad(n++)} title="Invoicing" intro={onFile ? undefined : "Where invoices should be addressed and sent."}>
        {onFile && (
          <Notice tone="info" title={`We already hold invoicing details for ${onFile.company || "your organisation"}`} testid="cm-on-file">
            They are filled in below from our client records. Please check them and correct anything that has changed; corrections update our records automatically.
          </Notice>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Company name to appear on invoices" required invalid={bad("invoice_company")}><TextInput value={a.invoice_company} onChange={(v) => set("invoice_company", v)} testid="cm-invoice-company" autoComplete="organization" /></Field>
          <Field label="Address line 1" required invalid={addrBad && !addr.line1}><TextInput value={addr.line1} onChange={setAddr("line1")} testid="cm-addr1" autoComplete="address-line1" /></Field>
          <Field label="Address line 2"><TextInput value={addr.line2} onChange={setAddr("line2")} autoComplete="address-line2" /></Field>
          <Field label="Town or city" required invalid={addrBad && !addr.city}><TextInput value={addr.city} onChange={setAddr("city")} testid="cm-city" autoComplete="address-level2" /></Field>
          <Field label="Postcode"><TextInput value={addr.postal_code} onChange={setAddr("postal_code")} testid="cm-postcode" autoComplete="postal-code" /></Field>
          <Field label="Country" required invalid={addrBad && !addr.country} group><CountrySelect value={addr.country} onChange={setAddr("country")} testid="cm-country" invalid={addrBad && !addr.country} /></Field>
          <InvoiceEmails a={a} set={set} bad={bad} />
        </div>
        <VatNote country={addr.country} />
      </Section>

      <Section n={pad(n++)} title="Keeping you informed" intro="How, and to whom, Medicys should send progress reports and deliverables.">
        <Field label="Which two days would you like a study progress report?" required={forSigning} group invalid={bad("update_frequency")} hint="Two progress reports a week are included in the costs; we recommend Tuesdays and Thursdays, but choose any two days that suit you. If you would like more frequent reports, let us know in the Special requests box at the end of this section and we will come back to you.">
          <div className="flex flex-wrap gap-2.5" data-testid="cm-update-days">
            {DAYS.map((d) => {
              const on = (a.update_frequency || []).includes(d);
              const locked = daysFull && !on;
              return (
                <label
                  key={d}
                  className={`inline-flex items-center gap-2.5 rounded-full border px-4 py-2.5 text-sm select-none transition-[background-color,border-color,color,opacity] ${
                    on ? "bg-[#1B4F8A]/5 border-[#1B4F8A] text-[#1B4F8A] font-medium cursor-pointer"
                      : locked ? "bg-stone-50 border-stone-200 text-stone-400 cursor-not-allowed opacity-70"
                        : "bg-white border-stone-300 text-stone-700 hover:border-[#1B4F8A] cursor-pointer"
                  }`}
                  title={locked ? "Two days are already selected. Unselect one to change." : undefined}
                >
                  <Checkbox checked={on} disabled={locked} onCheckedChange={() => toggleDay(d)} data-testid={`cm-day-${d}`} className="border-stone-400 data-[state=checked]:bg-[#1B4F8A] data-[state=checked]:border-[#1B4F8A]" /> {d}
                </label>
              );
            })}
          </div>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NameFields a={a} set={set} invalid={bad("client_pm")} firstKey="client_pm_first_name" lastKey="client_pm_last_name" fullKey="client_pm"
            labelFirst="Project main point of contact: first name" labelLast="Project main point of contact: surname" required hint="Usually your project manager." testid="cm-client-pm" />
          <Field label="Their email address" required invalid={bad("client_pm_email")}><TextInput type="email" value={a.client_pm_email} onChange={(v) => set("client_pm_email", v)} placeholder="name@company.com" testid="cm-client-pm-email" /></Field>
          <Field label="Their telephone number"><TextInput value={a.client_pm_phone} onChange={(v) => set("client_pm_phone", v)} placeholder="+44 20 0000 0000" testid="cm-client-pm-phone" autoComplete="tel" /></Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Who else, if anyone, should receive the study progress reports?" hint="Email addresses, separated by commas. Your main point of contact receives them automatically."><TextInput value={a.updates_emails} onChange={(v) => set("updates_emails", v)} placeholder="colleague@company.com, team@company.com" testid="cm-updates-emails" /></Field>
          <Field label="Who else, if anyone, should receive the deliverables?" hint="Leave blank if the deliverables should go to the same people as the progress reports."><TextInput value={a.deliverables_emails} onChange={(v) => set("deliverables_emails", v)} placeholder="colleague@company.com, team@company.com" testid="cm-deliverables-emails" /></Field>
        </div>
        <Field label="Special requests" hint="Optional. Preferences, extra progress reports, people to copy in, constraints, anything that helps us look after you.">
          <TextArea value={a.comments} onChange={(v) => set("comments", v)} testid="cm-comments" />
        </Field>
      </Section>
        </>
      )}
    </fieldset>
    </div>
  );
}
